// views/LandingPage/components/LandingPageUsagesList/index.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ReactDOM from "react-dom";
import styles from "./styles.module.scss";
import UsageCard from "./UsageCard";
import { filterUsagesByTypes } from "./filterUsages";

const LandingPageUsagesList = ({ 
  usageTypes = [],
  containerId = 'landing-page-usage-container'
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [container, setContainer] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(4);
  const sliderRef = useRef(null);
  const trackRef = useRef(null);
  const { t } = useTranslation("global");
  const navigate = useNavigate();

  // Mobile drag functionality
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Filter usages based on provided usage types
  const filteredUsages = useMemo(() => {
    return filterUsagesByTypes(usageTypes);
  }, [usageTypes]);

  // Calculate slides to show and card width based on screen size
  const calculateLayout = useCallback(() => {
    const isMobileView = window.innerWidth < 768;
    setIsMobile(isMobileView);
    
    let slides = 4;
    
    if (isMobileView) {
      slides = 1;
    } else if (window.innerWidth < 1024) {
      slides = Math.min(3, filteredUsages.length);
    } else {
      slides = Math.min(4, filteredUsages.length);
    }
    
    setSlidesToShow(slides);
    
    // Reset current slide if it exceeds max
    const maxSlide = Math.max(0, filteredUsages.length - slides);
    if (currentSlide > maxSlide) {
      setCurrentSlide(0);
    }
  }, [filteredUsages.length, currentSlide]);

  // Initial layout calculation and resize listener
  useEffect(() => {
    calculateLayout();
    
    const handleResize = () => {
      calculateLayout();
    };
    
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [calculateLayout]);

  // Reset current slide when filteredUsages changes
  useEffect(() => {
    setCurrentSlide(0);
  }, [filteredUsages]);

  // Find the container element
  useEffect(() => {
    const el = document.getElementById(containerId);
    if (el) {
      setContainer(el);
    }
  }, [containerId, filteredUsages]);

  // Mobile drag handlers
  const handleMouseDown = (e) => {
    if (!isMobile) return;
    setIsDragging(true);
    setStartX(e.pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    if (!isMobile) return;
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    if (!isMobile) return;
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !isMobile) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    sliderRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchStart = () => {
    if (!isMobile) return;
  };

  const handleTouchEnd = () => {
    if (!isMobile) return;
  };

  const handleTouchMove = () => {
    if (!isMobile) return;
  };

  // If no filtered usages or no container, don't render
  if (!filteredUsages.length || !container) {
    return null;
  }

  const totalSlides = filteredUsages.length;
  const maxSlideIndex = Math.max(0, totalSlides - slidesToShow);

  const goToSlide = (index) => {
    setCurrentSlide(Math.max(0, Math.min(index, maxSlideIndex)));
  };

  const nextSlide = () => {
    if (currentSlide < maxSlideIndex) {
      goToSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      goToSlide(currentSlide - 1);
    }
  };

  // Calculate card width based on screen size
  const getCardWidth = () => {
    if (isMobile) return window.innerWidth * 0.85;
    if (window.innerWidth < 1024) return 280;
    return 320;
  };

  const cardWidth = getCardWidth();
  const gap = isMobile ? 12 : 20;
  const slideWidth = cardWidth + gap;

  // Calculate the visible width of the slider
  const visibleWidth = slidesToShow * slideWidth - gap;

  const usageCards = filteredUsages.map((usage, index) => (
    <div 
      key={index} 
      className={styles.usageCardWrapper}
      style={{
        flex: `0 0 ${cardWidth}px`,
        width: `${cardWidth}px`,
        minWidth: `${cardWidth}px`,
        maxWidth: `${cardWidth}px`,
      }}
    >
      <UsageCard
        usageImg={usage.usageImg}
        usageName={t(usage.usageKey)}
        usageMinPrice={`${t("space.from")} ${usage.usageMinPrice}`}
        usageLink={usage.usageLink}
      />
    </div>
  ));

  // Calculate the transform value - only move by full slides
  const transformValue = -(currentSlide * slideWidth);

  const sliderContent = (
    <div className={`${styles.landingUsagesContainer} ${isMobile ? styles.mobile : ''}`}>
      {!isMobile && (
        <div className={styles.customSlider}>
          <div 
            className={styles.sliderViewport}
            style={{
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <div 
              ref={trackRef}
              className={styles.sliderTrack}
              style={{
                transform: `translateX(${transformValue}px)`,
                transition: 'transform 0.5s ease',
                display: 'flex',
                gap: `${gap}px`,
                width: `${totalSlides * slideWidth}px`,
              }}
            >
              {usageCards}
            </div>
          </div>
          
          {/* Navigation Arrows */}
          {totalSlides > slidesToShow && (
            <>
              <button 
                className={`${styles.sliderArrow} ${styles.sliderArrowLeft} ${currentSlide === 0 ? styles.disabled : ''}`}
                onClick={prevSlide}
                disabled={currentSlide === 0}
                aria-label="Previous slides"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button 
                className={`${styles.sliderArrow} ${styles.sliderArrowRight} ${currentSlide >= maxSlideIndex ? styles.disabled : ''}`}
                onClick={nextSlide}
                disabled={currentSlide >= maxSlideIndex}
                aria-label="Next slides"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </>
          )}
        </div>
      )}

      {/* Mobile: Use custom scrolling */}
      {isMobile && (
        <div 
          ref={sliderRef}
          className={styles.mobileTrack}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchMove={handleTouchMove}
        >
          {usageCards}
        </div>
      )}
    </div>
  );

  // Use ReactDOM.createPortal to render at the container location
  return ReactDOM.createPortal(sliderContent, container);
};

export default LandingPageUsagesList;